import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are two ways to implement feature flags in React:`}</p>
    <ol>
      <li parentName="ol">{`Using hooks.`}</li>
      <li parentName="ol">{`Using the `}<inlineCode parentName="li">{`<PostHogFeature>`}</inlineCode>{` component.`}</li>
    </ol>
    <h3 {...{
      "id": "method-1-using-hooks"
    }}>{`Method 1: Using hooks`}</h3>
    <p>{`PostHog provides several hooks to make it easy to use feature flags in your React app.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Hook`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useFeatureFlagEnabled`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns a boolean indicating whether the feature flag is enabled.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useFeatureFlagPayload`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns the payload of the feature flag.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useFeatureFlagVariantKey`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns the variant key of the feature flag.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`useActiveFeatureFlags`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Returns an array of active feature flags.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "example-1-using-a-boolean-feature-flag"
    }}>{`Example 1: Using a boolean feature flag`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { useFeatureFlagEnabled } from 'posthog-js/react'

function App() {
  const showWelcomeMessage = useFeatureFlagEnabled('flag-key')

  return (
    <div className="App">
      {
        showWelcomeMessage ? (
          <div>
            <h1>Welcome!</h1>
            <p>Thanks for trying out our feature flags.</p>
          </div>
        ) : (
          <div>
            <h2>No welcome message</h2>
            <p>Because the feature flag evaluated to false.</p>
          </div>
        )
      }
    </div>
  );
}

export default App;
`}</code></pre>
    <h4 {...{
      "id": "example-2-using-a-multivariate-feature-flag"
    }}>{`Example 2: Using a multivariate feature flag`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { useState, useEffect } from 'react'

function App() {
  const variantKey = useFeatureFlagVariantKey('show-welcome-message')
  const [ welcomeMessage, setWelcomeMessage ] = useState('')

  useEffect(() => {
    if (variantKey === 'variant-a') {
      setWelcomeMessage('Welcome to the Alpha!')
    } else if (variantKey === 'variant-b') {
      setWelcomeMessage('Welcome to the Beta!')
    }
  }, [variantKey])

  return (
    <div className="App">
      {
        welcomeMessage ? (
          <div>
            <h1>{welcomeMessage}</h1>
            <p>Thanks for trying out our feature flags.</p>
          </div>
        ) : (
          <div>
            <h2>No welcome message</h2>
            <p>Because the feature flag evaluated to false.</p>
          </div>
        )
      }
    </div>
  );
}

export default App;
`}</code></pre>
    <h4 {...{
      "id": "example-3-using-a-flag-payload"
    }}>{`Example 3: Using a flag payload`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { useFeatureFlagPayload } from 'posthog-js/react'

function App() {
  const payload = useFeatureFlagPayload('show-welcome-message')

    return (
                <>
                {
                    payload?.welcomeMessage ? (
                        <div className="welcome-message">
                            <h2>{payload?.welcomeTitle}</h2>
                            <p>{payload.welcomeMessage}</p>
                        </div>
                    ) : <div>
                        <h2>No welcome message</h2>
                        <p>Because the feature flag evaluated to false.</p>
                    </div>
                }
        </>
    )
}
`}</code></pre>
    <h3 {...{
      "id": "method-2-using-the-posthogfeature-component"
    }}>{`Method 2: Using the PostHogFeature component`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`PostHogFeature`}</inlineCode>{` component simplifies code by handling feature flag related logic. `}</p>
    <p>{`It also automatically captures metrics, like how many times a user interacts with this feature.`}</p>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` You still need the `}<a parentName="p" {...{
          "href": "/docs/libraries/react#installation"
        }}><inlineCode parentName="a">{`PostHogProvider`}</inlineCode></a>{` at the top level for this to work.`}</p>
    </blockquote>
    <p>{`Here is an example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { PostHogFeature } from 'posthog-js/react'

function App() {

    return (
        <PostHogFeature flag='show-welcome-message' match={true}>
            <div>
                <h1>Hello</h1>
                <p>Thanks for trying out our feature flags.</p>
            </div>
        </PostHogFeature>
    )
}
`}</code></pre>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`match`}</inlineCode>{` on the component can be either `}<inlineCode parentName="p">{`true`}</inlineCode>{`, or the variant key, to match on a specific variant.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If you also want to show a default message, you can pass these in the `}<inlineCode parentName="p">{`fallback`}</inlineCode>{` attribute.`}</p>
      </li>
    </ul>
    <p>{`If you wish to customise logic around when the component is considered visible, you can pass in `}<inlineCode parentName="p">{`visibilityObserverOptions`}</inlineCode>{` to the feature. These take the same options as the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API"
      }}>{`IntersectionObserver API`}</a>{`. By default, we use a threshold of 0.1. `}</p>
    <h4 {...{
      "id": "payloads"
    }}>{`Payloads`}</h4>
    <p>{`If your flag has a payload, you can pass a function to children whose first argument is the payload. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`import { PostHogFeature } from 'posthog-js/react'

function App() {

    return (
        <PostHogFeature flag='show-welcome-message' match={true}>
           {(payload) => {
                return (
                    <div>
                        <h1>{payload.welcomeMessage}</h1>
                        <p>Thanks for trying out our feature flags.</p>
                    </div>
                )
           }}
        </PostHogFeature>
    )
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      